import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // 初始化 - 检查是否已经登录
  useEffect(() => {
    const checkLoggedIn = async () => {
      const token = localStorage.getItem('token');
      const cachedUser = localStorage.getItem('user');

      if (token) {
        // 乐观策略：如果有缓存的用户信息，立即设置为已登录
        if (cachedUser) {
          try {
            const parsedUser = JSON.parse(cachedUser);
            setUser(parsedUser);
            setLoading(false); // 立即停止加载

            // 设置语言
            if (!localStorage.getItem('i18nextLng') && parsedUser.language) {
              i18n.changeLanguage(parsedUser.language);
              localStorage.setItem('i18nextLng', parsedUser.language);
            }
          } catch (e) {
            console.error('解析缓存用户数据失败', e);
          }
        }

        try {
          // 后台验证 Token 并获取最新用户信息
          const response = await AuthAPI.getCurrentUser();
          const userData = response.data.data;

          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData)); // 更新缓存

          // 设置用户语言 - 优先使用localStorage中的设置
          const savedLanguage = localStorage.getItem('i18nextLng');
          if (!savedLanguage && userData.language) {
            i18n.changeLanguage(userData.language);
            localStorage.setItem('i18nextLng', userData.language);
          }

          // 如果用户在登录页或注册页，则根据角色重定向
          const currentPath = window.location.pathname;
          if (currentPath === '/login' || currentPath === '/register' || currentPath === '/') {
            if (userData.role === 'superadmin') {
              navigate('/superadmin');
            } else if (userData.role === 'admin') {
              navigate('/admin');
            } else {
              navigate('/dashboard');
            }
          }
        } catch (err) {
          console.error('获取当前用户时出错:', err);
          // 如果后台验证失败（Token无效），则清除状态并登出
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
          if (window.location.pathname !== '/login') {
            navigate('/login');
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    checkLoggedIn();
  }, [i18n, navigate]);

  // 注册
  const register = async (userData) => {
    try {
      setError(null);
      const response = await AuthAPI.register(userData);
      const { token, user: registeredUser } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(registeredUser));

      setUser(registeredUser);

      // 设置用户语言
      if (registeredUser.language) {
        i18n.changeLanguage(registeredUser.language);
        localStorage.setItem('i18nextLng', registeredUser.language);
      }

      // 根据用户角色重定向到相应的仪表盘
      if (registeredUser.role === 'superadmin') {
        navigate('/superadmin');
      } else if (registeredUser.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || t('auth.registerFailed');
      setError(message);
      return { success: false, message };
    }
  };

  const login = async (credentials) => {
    try {
      setError(null);
      // credentials already contains language if passed from Login.js
      const response = await AuthAPI.login(credentials);
      const { token, user: loggedInUser } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(loggedInUser));

      setUser(loggedInUser);

      // 设置用户语言
      if (loggedInUser.language) {
        i18n.changeLanguage(loggedInUser.language);
        localStorage.setItem('i18nextLng', loggedInUser.language);
      }

      // 根据用户角色重定向到相应的仪表盘
      if (loggedInUser.role === 'superadmin') {
        navigate('/superadmin');
      } else if (loggedInUser.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '登录时出错';
      setError(message);
      return { success: false, message };
    }
  };

  // 退出登录
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  // 更新用户语言
  const updateLanguage = async (language) => {
    try {
      setError(null);
      const response = await AuthAPI.updateLanguage(language);
      const updatedUser = response.data.data;

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      i18n.changeLanguage(language);
      localStorage.setItem('i18nextLng', language);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '更新语言时出错';
      setError(message);
      return { success: false, message };
    }
  };

  // 更新用户资料 (用户名、头像、时区等)
  const updateProfile = async (profileData) => {
    try {
      setError(null);
      const response = await AuthAPI.updateProfile(profileData);
      const updatedUser = response.data.data;

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '更新资料时出错';
      setError(message);
      return { success: false, message };
    }
  };

  // 发送验证码
  const sendVerificationCode = async (type) => {
    try {
      setError(null);
      await AuthAPI.sendVerificationCode(type);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '发送验证码失败';
      setError(message);
      return { success: false, message };
    }
  };

  // 修改密码
  const changePassword = async (currentPassword, newPassword, code) => {
    try {
      setError(null);
      await AuthAPI.changePassword({ currentPassword, newPassword, code });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '修改密码时出错';
      setError(message);
      return { success: false, message };
    }
  };

  // 上传头像
  const uploadAvatar = async (file) => {
    try {
      setError(null);
      const response = await AuthAPI.uploadAvatar(file);
      const { user: updatedUser } = response.data.data;

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      return { success: true, avatar: response.data.data.avatar };
    } catch (err) {
      const message = err.response?.data?.message || '上传头像时出错';
      setError(message);
      return { success: false, message };
    }
  };

  // 删除账户
  const deleteAccount = async (code) => {
    try {
      setError(null);
      await AuthAPI.deleteAccount(code);
      // 删除成功后执行登出清理
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      navigate('/login');
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.message || '删除账户时出错';
      setError(message);
      return { success: false, message };
    }
  };

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout,
    updateLanguage,
    updateProfile,
    sendVerificationCode,
    changePassword,
    uploadAvatar,
    deleteAccount,
    isAuthenticated: !!user
  }), [user, loading, error]);


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 自定义钩子，用于在组件中使用认证上下文
export const useAuth = () => useContext(AuthContext);

export default AuthContext; 